import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles, siteConfig } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-200 text-foreground-400 mx-auto mb-4">
              <i className="ri-file-search-line text-3xl w-10 h-10 flex items-center justify-center"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-500 mb-6">该文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary-500 text-background-50 font-medium text-sm transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: '体育资讯', href: '/article/app-download-guide' },
    { label: article.category, href: `/category/${
      article.category === '足球' ? 'football' :
      article.category === '篮球' ? 'basketball' :
      article.category === '电竞' ? 'esports' :
      article.category === '网球' ? 'tennis' :
      article.category === '使用教程' ? 'football' :
      article.category === '投注指南' ? 'football' : 'football'
    }` },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.name,
    },
    url: `https://www.kaiyunsports.com/article/${article.slug}`,
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <div className="bg-background-100">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="w-full py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                  <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                    {article.category}
                  </span>
                  <span className="text-xs text-foreground-400">{article.date}</span>
                  <span className="flex items-center gap-1 text-xs text-foreground-400">
                    <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
                    {article.readTime}
                  </span>
                </div>

                <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
                  {article.title}
                </h1>

                <p className="text-base text-foreground-500 leading-relaxed">
                  {article.excerpt}
                </p>
              </div>

              <div className="relative h-64 md:h-96 rounded-xl overflow-hidden mb-10">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>

              <div className="prose prose-sm md:prose-base max-w-none text-foreground-700">
                <p className="text-foreground-600 leading-relaxed mb-6">
                  开云体育始终致力于为用户提供最优质的体育竞技体验。本文将从多个维度为您深入分析相关赛事与资讯，
                  帮助您更好地了解体育竞技的魅力所在。
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  赛事背景与概况
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  随着全球体育产业的不断发展，越来越多的体育爱好者通过开云体育app获取最新赛事资讯与数据分析。
                  平台覆盖足球、篮球、电竞、网球等多个热门项目，为用户提供全方位的体育服务体验。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  开云体育app下载安装后，用户可以直接在手机上查看实时比分、赛事直播、专业分析等内容，
                  随时随地掌握体坛动态。平台采用先进的数据加密技术，确保用户信息安全。
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  核心数据分析
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  通过开云体育app的数据分析功能，用户可以查看详细的赛事统计数据，包括球队历史交锋记录、
                  近期战绩走势、球员状态评估等多个维度。这些数据为赛事预测提供了有力支撑。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  平台采用AI智能算法，结合大数据分析，为用户提供专业的赛事预测建议。
                  同时，用户也可以通过社区功能与其他体育爱好者交流心得，分享观点。
                </p>

                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mt-10 mb-4">
                  观赛指南与建议
                </h2>
                <p className="text-foreground-600 leading-relaxed mb-4">
                  对于新用户来说，开云体育app提供了友好的引导流程。下载安装后，用户可以根据个人偏好
                  设置关注球队和赛事类型，系统会自动推送相关资讯和直播提醒。
                </p>
                <p className="text-foreground-600 leading-relaxed mb-6">
                  建议用户在使用过程中充分利用平台提供的各项功能，包括赛事回放、数据对比、
                  专家分析等，以获得最佳的体育竞技体验。
                </p>

                <div className="mt-10 p-6 rounded-xl bg-primary-50 border border-primary-100/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 flex-shrink-0">
                      <i className="ri-information-line text-lg w-5 h-5 flex items-center justify-center"></i>
                    </div>
                    <div>
                      <p className="font-semibold text-foreground-900 mb-1 text-sm">开云体育温馨提示</p>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        体育竞技重在参与和体验，请理性对待赛事结果。开云体育倡导健康、理性的体育娱乐方式，
                        如需帮助请联系在线客服。
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex flex-wrap gap-2 mb-8">
                  <span className="text-xs text-foreground-400">标签：</span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">开云体育app下载</span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">{article.category}</span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">赛事分析</span>
                  <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">体育资讯</span>
                </div>

                {relatedArticles.length > 0 && (
                  <div>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mb-5">相关文章推荐</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {relatedArticles.map((rel) => (
                        <Link
                          key={rel.slug}
                          to={`/article/${rel.slug}`}
                          className="group block rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200/60 bg-background-50 transition-all duration-300 no-underline"
                        >
                          <div className="relative h-36 overflow-hidden">
                            <img
                              src={rel.image}
                              alt={rel.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                          <div className="p-4">
                            <h4 className="font-medium text-foreground-900 text-xs md:text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                              {rel.title}
                            </h4>
                            <p className="text-xs text-foreground-400 mt-1">{rel.date}</p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}